import { Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { NGXLogger } from 'ngx-logger';
import { ToastrService } from 'ngx-toastr';
import { OverheadCategoryMonthlyBudget } from 'src/app/models/Financial/OverheadCategoryMonthlyBudget';

import { FinancialService } from '../financial.service';

@Component({
    selector: 'app-overhead-category-budget-modal',
    templateUrl: './overhead-category-budget-modal.component.html',
    styleUrls: ['./overhead-category-budget-modal.component.scss'],
})
export class OverheadCategoryBudgetModalComponent implements OnInit {
    @Input() categoryId: string;
    @Input() categoryName: string;
    @Input() year: number;

    loading = false;
    saving = false;
    mode: 'TOTAL' | 'MONTHLY' = 'TOTAL';
    totalBudget = 0;
    editingTotalBudget = false;

    readonly months = [
        { value: 1, label: 'January' },
        { value: 2, label: 'February' },
        { value: 3, label: 'March' },
        { value: 4, label: 'April' },
        { value: 5, label: 'May' },
        { value: 6, label: 'June' },
        { value: 7, label: 'July' },
        { value: 8, label: 'August' },
        { value: 9, label: 'September' },
        { value: 10, label: 'October' },
        { value: 11, label: 'November' },
        { value: 12, label: 'December' },
    ];

    monthlyBudgets: Array<{ month: number; label: string; budget: number }> = [];

    constructor(
        public activeModal: NgbActiveModal,
        private financialService: FinancialService,
        private toastr: ToastrService,
        private logger: NGXLogger,
    ) {}

    ngOnInit(): void {
        this.monthlyBudgets = this.months.map((month) => ({
            month: month.value,
            label: month.label,
            budget: 0,
        }));
        this.loadBudgets();
    }

    onModeChanged(): void {
        if (this.mode === 'TOTAL') {
            this.onTotalBudgetChanged();
        }
    }

    onTotalBudgetChanged(): void {
        if (this.mode !== 'TOTAL') {
            return;
        }

        const distributed = this.distributeTotalAcrossMonths(this.totalBudget || 0);
        this.monthlyBudgets = this.monthlyBudgets.map((item, index) => ({
            ...item,
            budget: distributed[index],
        }));
    }

    onTotalBudgetInputChanged(value: string | number): void {
        this.totalBudget = this.roundToFiveDecimals(this.parseDecimalInput(value));
        this.onTotalBudgetChanged();
    }

    onMonthlyBudgetInputChanged(item: { month: number; label: string; budget: number }, value: string | number): void {
        item.budget = this.roundToFiveDecimals(this.parseDecimalInput(value));
    }

    save(): void {
        if (!this.categoryId) {
            this.toastr.error('Overhead category is missing.');
            return;
        }

        if (this.mode === 'TOTAL') {
            this.onTotalBudgetChanged();
        }

        const payload = this.monthlyBudgets.map((item) => ({
            year: this.year,
            month: item.month,
            budget: this.roundToFiveDecimals(item.budget || 0),
        }));

        this.saving = true;
        this.financialService
            .upsertOverheadCategoryMonthlyBudgets(this.categoryId, payload)
            .subscribe({
                next: () => {
                    this.toastr.success('Budget saved.');
                    this.activeModal.close(true);
                },
                error: (error) => {
                    this.logger.error('Saving overhead monthly budget failed', error);
                    this.toastr.error('Saving budget failed.');
                    this.saving = false;
                },
            });
    }

    deleteBudget(): void {
        if (!this.categoryId) {
            this.toastr.error('Overhead category is missing.');
            return;
        }

        this.saving = true;
        this.financialService.deleteOverheadCategoryMonthlyBudgetsForYear(this.categoryId, this.year).subscribe({
            next: () => {
                this.toastr.success('Budget removed.');
                this.activeModal.close(true);
            },
            error: (error) => {
                this.logger.error('Deleting overhead monthly budget failed', error);
                this.toastr.error('Deleting budget failed.');
                this.saving = false;
            },
        });
    }

    private loadBudgets(): void {
        if (!this.categoryId || !this.year) {
            return;
        }

        this.loading = true;
        this.financialService.getOverheadCategoryMonthlyBudgets(this.categoryId, this.year).subscribe({
            next: (budgets) => {
                this.applyBudgets(budgets || []);
                this.loading = false;
            },
            error: (error) => {
                this.logger.error('Loading overhead monthly budgets failed', error);
                this.toastr.error('Loading budgets failed.');
                this.applyBudgets([]);
                this.loading = false;
            },
        });
    }

    private applyBudgets(budgets: OverheadCategoryMonthlyBudget[]): void {
        const mapByMonth = new Map<number, number>();
        budgets.forEach((item) => {
            mapByMonth.set(item.month, Number(item.budget || 0));
        });

        this.monthlyBudgets = this.months.map((month) => ({
            month: month.value,
            label: month.label,
            budget: mapByMonth.get(month.value) ?? 0,
        }));

        this.totalBudget = this.currentYearTotal;
    }

    private roundToFiveDecimals(value: number): number {
        return Math.round((Number(value) + Number.EPSILON) * 100000) / 100000;
    }

    formatToTwoDecimals(value: number): string {
        return Number(value || 0).toFixed(2);
    }

    get currentYearTotal(): number {
        return this.roundToFiveDecimals(this.monthlyBudgets.reduce((sum, item) => sum + Number(item.budget || 0), 0));
    }

    private distributeTotalAcrossMonths(total: number): number[] {
        const monthlyValue = this.roundToFiveDecimals(Number(total || 0) / 12);
        return Array.from({ length: 12 }, () => monthlyValue);
    }

    private parseDecimalInput(value: string | number): number {
        if (typeof value === 'number') {
            return Number.isFinite(value) ? value : 0;
        }

        const normalized = String(value || '')
            .trim()
            .replace(',', '.');

        const parsed = Number(normalized);
        return Number.isFinite(parsed) ? parsed : 0;
    }
}
